export interface LegacyAction {
    name: LegacyActionName.Reprocess | LegacyActionName.Acknowledge;
    type: 'each';
    handler: {
        method: 'POST' | 'PUT' | 'DELETE';
        url: string;
        body: LegacyActionHandlerBody;
    };
}

export interface LegacyActionHandlerBody {
    type: 'array' | 'object' | 'wrapped-array';
    contentType?: string;
    wrapperPropertyName?: string;
    keyField?: string;
    items?: {
        type?: 'object';
        properties: Record<string, string>;
    };
    properties?: {
        [key: string]: unknown;
    };
}

// Action naming convention:
//  - *propertyName means this will be the name of a property that ends up in the payload sent to the backend
//  - *fieldName means this is the name of the property on the row for getting values to put into the payload
//  - *wrapperPropertyName means this will wrap the payload in an object with this property name

export interface Action {
    name: string;
    type: 'cell-level-edit' | 'each';
    handler: CellLevelEditActionHandler | RowLevelEditActionHandler;
    requiresUserComment?: { [key: string]: string };
}

interface RowLevelEditActionHandler {
    method: 'POST' | 'PUT' | 'DELETE';
    url: string;
    body: ActionObjectBody | ActionEntityBody | ActionArrayBody | ActionWrappedArrayBody;
}

export interface ActionObjectBody {
    type: 'object';
    wrapperPropertyName: string;
    rowKeyFieldName: string;
    rowValuePropertyName: string;
    rowValueFieldName: string;
}

export interface ActionEntityBody {
    type: 'entity';
    keyPropertyName: string;
    keyFieldName: string;
    valuePropertyName: string;
    valueFieldName: string;
}

interface ActionArrayBody {
    type: 'array';
    rowFieldName: string;
}

export interface ActionWrappedArrayBody {
    type: 'wrapped-array';
    wrapperPropertyName: string;
    rowFieldName: string;
}

export interface CellLevelEditActionHandler {
    fieldName: string;
    method: 'POST' | 'PUT' | 'DELETE';
    url: string;
    body: ActionObjectBody | ActionEntityBody;
    submitInBatch: boolean;
    picklistRoute?: string;
}

export interface ActionState {
    [key: string]: (Action | LegacyAction)[];
}

export enum LegacyActionName {
    Acknowledge = 'acknowledge',
    Reprocess = 'reprocess',
}

export interface AcknowledgeActionResponse {
    success: boolean;
}

export interface ReprocessActionResponse {
    success: boolean;
}

interface EntityActionResponse {
    success: boolean;
    message?: string;
}

interface EditActionResponseBody {
    saved_fields: string[];
    status: string;
    status_msg: string;
}

interface EditActionResponse {
    trades: Record<string, EditActionResponseBody>;
}

interface ReleaseActionBodyResponse {
    status: string;
    message?: string;
}

export interface BatchAction {
    startValue: string;
    latestValue: string;
    method: 'POST' | 'PUT' | 'DELETE';
    url: string;
    payload: Record<string, unknown>;
}

export type ReleaseActionResponse = Record<string, ReleaseActionBodyResponse>;

export type ActionResponse = AcknowledgeActionResponse |
ReprocessActionResponse |
EditActionResponse |
ReleaseActionResponse |
EntityActionResponse;

export const actionsWithPartialUrls = [LegacyActionName.Acknowledge, LegacyActionName.Reprocess];

export function isActionEntityBody(value: unknown): value is ActionEntityBody {
    return !!value && (value as ActionEntityBody).type === 'entity';
}

export function isActionObjectBody(value: unknown): value is ActionObjectBody {
    return !!value && (value as ActionObjectBody).type === 'object';
}

export function isActionWrappedArrayBody(value: unknown): value is ActionWrappedArrayBody {
    return !!value && (value as ActionWrappedArrayBody).type === 'wrapped-array';
}

export function isLegacyAction(value: unknown): value is LegacyAction {
    return !!value && [LegacyActionName.Reprocess, LegacyActionName.Acknowledge].includes((value as LegacyAction).name);
}

export function isCellLevelEditActionHandler(value: unknown): value is CellLevelEditActionHandler {
    return !!value && !!(value as CellLevelEditActionHandler).fieldName;
}
